/**
 * Определение локали i18n: явный выбор пользователя (localStorage) → поддомен
 * (Фаза 4, решение 2.2) → `ru` по умолчанию.
 *
 * Контракт поддомена: `en.<apex>` → en, `cn.<apex>` → cn, иначе (apex / `www.` /
 * `localhost` / пусто) → ru. `detectLocale` чистая и детерминированная, не
 * обращается к `window` — hostname передаётся параметром (table-driven тесты без
 * моков). Регистронезависима.
 *
 * 0.14.2 (игротест, замечание 2): смена языка больше НЕ навигирует на поддомен —
 * полная перезагрузка на другой origin теряла сессию (host-only cookie
 * `kikayoo_session`), sessionStorage host/player-флоу и скролл, из-за чего
 * пользователь «вылетал» на главную/login. Теперь выбор меняется на лету
 * (`i18n.changeLanguage`) и фиксируется в localStorage: он переживает F5 и
 * приоритетнее поддомена (явное желание пользователя главнее канала входа).
 * Поддомены `en.`/`cn.` продолжают работать как дефолт-локаль для новых
 * посетителей без сохранённого выбора.
 *
 * См. [`phase-4-plan.md`](../../docs/dev/phase-4-plan.md) §2.2.
 */

export type Locale = 'ru' | 'en' | 'cn';

/** Локаль по умолчанию (apex / неизвестный поддомен / SSR без window). */
export const DEFAULT_LOCALE: Locale = 'ru';

/** Полный набор поддерживаемых локалей Фазы 4. */
export const SUPPORTED_LOCALES: readonly Locale[] = ['ru', 'en', 'cn'] as const;

/** Ключ localStorage для явного выбора языка пользователем (0.14.2). */
export const LOCALE_STORAGE_KEY = 'kikayoo.locale';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Явно выбранный пользователем язык из localStorage или null (нет выбора /
 * SSR без `window` / мусорное значение / заблокированное хранилище).
 */
export function readStoredLocale(): Locale | null {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return isLocale(raw) ? raw : null;
  } catch {
    // Приватный режим/quota — хранилище недоступно: работаем как без выбора.
    return null;
  }
}

/**
 * Фиксирует явный выбор языка (localStorage). Ошибки хранилища глотаются:
 * невозможность сохранить не должна ломать смену языка в текущей сессии.
 */
export function storeLocale(locale: Locale): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // См. readStoredLocale: приватный режим и т.п.
  }
}

/**
 * Определяет локаль по hostname: префикс `en.`/`cn.` (case-insensitive) выбирает
 * соответствующий язык; всё прочее (включая пустую строку, `localhost`, `www.` и
 * произвольные поддомены) → `ru`.
 *
 * @param hostname — значение `window.location.hostname` (или пустая строка в SSR).
 */
export function detectLocale(hostname: string): Locale {
  const h = hostname.toLowerCase();
  if (h.startsWith('en.')) return 'en';
  if (h.startsWith('cn.')) return 'cn';
  return DEFAULT_LOCALE;
}

/**
 * Стартовая локаль приложения (0.14.2): явный выбор пользователя из localStorage
 * приоритетнее поддомена; без выбора — поддомен (Фаза 4), без поддомена — `ru`.
 */
export function resolveInitialLocale(hostname: string): Locale {
  return readStoredLocale() ?? detectLocale(hostname);
}
